import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

// Default SEO configuration for Molomo NR
const defaultSEO = {
  siteName: 'Molomo NR Trading and Projects',
  siteUrl: 'https://www.molomonr.co.za',
  logo: 'https://www.molomonr.co.za/logo.webp',
  telephone: '+27-82-971-3299',
  email: 'nhlapolindiwe4@gmail.com',
  address: {
    street: '7323 Zone 12 ext',
    city: 'Sebokeng',
    province: 'Gauteng',
    postalCode: '1983',
    country: 'ZA'
  },
  social: {
    facebook: 'https://www.facebook.com/molomonr',
    instagram: 'https://www.instagram.com/molomonr',
    linkedin: 'https://www.linkedin.com/company/molomonr',
    whatsapp: 'https://api.whatsapp.com/send?phone=27829713299'
  }
};

// Organization Schema
const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${defaultSEO.siteUrl}/#organization`,
  name: defaultSEO.siteName,
  url: defaultSEO.siteUrl,
  logo: defaultSEO.logo,
  image: defaultSEO.logo,
  description: 'Industrial storage solutions for warehouses, retail, and manufacturing. B-BBEE Level 1 certified. Pallet racking, shelving, mezzanine floors, and storage systems across South Africa.',
  slogan: 'Storage That Works for You',
  telephone: defaultSEO.telephone,
  email: defaultSEO.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: defaultSEO.address.street,
    addressLocality: defaultSEO.address.city,
    addressRegion: defaultSEO.address.province,
    postalCode: defaultSEO.address.postalCode,
    addressCountry: defaultSEO.address.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '-26.6167',
    longitude: '27.8500'
  },
  sameAs: [
    defaultSEO.social.facebook,
    defaultSEO.social.instagram,
    defaultSEO.social.linkedin
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Industrial Storage Solutions',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Pallet Racking Systems',
          description: 'Selective, drive-in, cantilever and push-back racking configurations'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Industrial Shelving',
          description: 'Heavy-duty shelving for warehouses, archives, and retail'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Mezzanine Floors',
          description: 'Custom mezzanine floors to maximize vertical space'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Lockers and Storage',
          description: 'Secure lockers, cabinets and small-parts storage'
        }
      }
    ]
  }
});

// Website Schema
const generateWebsiteSchema = (pathname) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${defaultSEO.siteUrl}/#website`,
  name: defaultSEO.siteName,
  url: defaultSEO.siteUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${defaultSEO.siteUrl}/search?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
});

// Local Business Schema
const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['Organization', 'LocalBusiness'],
  '@id': `${defaultSEO.siteUrl}/#localbusiness`,
  name: defaultSEO.siteName,
  image: defaultSEO.logo,
  url: defaultSEO.siteUrl,
  telephone: defaultSEO.telephone,
  email: defaultSEO.email,
  priceRange: '$$$',
  currenciesAccepted: 'ZAR',
  paymentAccepted: 'Cash, Credit Card, Bank Transfer, EFT',
  address: {
    '@type': 'PostalAddress',
    streetAddress: defaultSEO.address.street,
    addressLocality: defaultSEO.address.city,
    addressRegion: defaultSEO.address.province,
    postalCode: defaultSEO.address.postalCode,
    addressCountry: defaultSEO.address.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -26.6167,
    longitude: 27.8500
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00'
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '09:00',
      closes: '13:00'
    }
  ],
  sameAs: [
    defaultSEO.social.facebook,
    defaultSEO.social.instagram,
    defaultSEO.social.linkedin
  ]
});

// Breadcrumb Schema
const generateBreadcrumbSchema = (pathname) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: defaultSEO.siteUrl
      }
    ]
  };

  let currentPath = '';
  paths.forEach((path, index) => {
    currentPath += `/${path}`;
    const name = path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');
    breadcrumbs.itemListElement.push({
      '@type': 'ListItem',
      position: index + 2,
      name: name,
      item: `${defaultSEO.siteUrl}${currentPath}`
    });
  });

  return breadcrumbs;
};

const SEOLayout = ({ 
  title,
  description,
  keywords = [],
  image = '/logo.webp',
  type = 'website',
  children,
  noIndex = false
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const canonicalUrl = `${defaultSEO.siteUrl}${pathname}`;
  const fullImageUrl = image.startsWith('http') ? image : `${defaultSEO.siteUrl}${image}`;

  // Default meta
  const metaTitle = title 
    ? `${title} | ${defaultSEO.siteName}`
    : `${defaultSEO.siteName} - Industrial Storage Solutions South Africa`;
    
  const metaDescription = description || 
    'B-BBEE Level 1 certified industrial storage solutions. Pallet racking, shelving, mezzanine floors & lockers. Serving warehouses, retail & manufacturing across South Africa.';

  const metaKeywords = [

  // ============================================================
  // USER SEARCH-STYLE KEYWORDS FOR MOLOMO NR
  // How Real People Actually Type in Google
  // ============================================================

  // ==========================================================
  // HIGH-INTENT COMMERCIAL (Ready to Buy)
  // ==========================================================

  // Pallet Racking - Commercial Intent
  'pallet racking suppliers near me',
  'pallet racking companies in johannesburg',
  'where to buy pallet racking in south africa',
  'pallet racking installers gauteng',
  'pallet racking for sale johannesburg',
  'industrial racking suppliers near me',
  'warehouse racking companies pretoria',
  'heavy duty pallet racking suppliers',
  'buy pallet racking systems south africa',
  'pallet racking installation services',
  'racking suppliers in sebokeng',
  'who sells pallet racking in vereeniging',
  'best pallet racking company gauteng',
  'affordable pallet racking johannesburg',
  'pallet racking quotes south africa',

  // Shelving - Commercial Intent
  'industrial shelving suppliers near me',
  'where to buy industrial shelving',
  'shelving companies in johannesburg',
  'warehouse shelving suppliers gauteng',
  'metal shelving for sale pretoria',
  'industrial shelving installation',
  'buy shelving systems south africa',
  'heavy duty shelving suppliers',
  'shelving for warehouse near me',
  'steel shelving companies durban',
  'industrial shelving prices south africa',
  'shelving suppliers cape town',
  'boltless shelving suppliers',
  'long span shelving for sale',
  'shelving installation services',

  // Mezzanine - Commercial Intent
  'mezzanine floor suppliers near me',
  'mezzanine floor companies johannesburg',
  'mezzanine floor installation pretoria',
  'how much does a mezzanine floor cost',
  'mezzanine floor builders south africa',
  'buy mezzanine floor gauteng',
  'mezzanine floor contractors near me',
  'warehouse mezzanine suppliers',
  'mezzanine floor installation cost',
  'who installs mezzanine floors',
  'mezzanine floor price per square meter',
  'mezzanine floor companies durban',
  'structural mezzanine suppliers',
  'rack supported mezzanine installers',
  'mezzanine floor quotes south africa',

  // Storage Solutions - Commercial Intent
  'storage solution companies near me',
  'warehouse storage systems south africa',
  'industrial storage suppliers johannesburg',
  'storage system installation gauteng',
  'warehouse design services pretoria',
  'storage consultants south africa',
  'warehouse optimization companies',
  'industrial storage solutions near me',
  'storage system suppliers durban',
  'warehouse racking and shelving',
  'complete storage solutions gauteng',
  'storage facility design south africa',
  'warehouse layout consultants',
  'storage system installers',
  'industrial storage design',

  // ==========================================================
  // QUESTION-BASED SEARCHES (Informational)
  // ==========================================================

  // Cost Questions
  'how much does pallet racking cost',
  'how much is a mezzanine floor per square meter',
  'what is the price of industrial shelving',
  'how much to install warehouse racking',
  'what is the cost of shelving installation',
  'how much does storage system cost',
  'what is the price of mezzanine installation',
  'how much for warehouse shelving',
  'what does industrial storage cost',
  'how much is racking per bay',

  // Product Comparison Questions
  'what is the best type of pallet racking',
  'which shelving system is best for warehouse',
  'what is better bolted or boltless shelving',
  'selective vs drive in racking which is better',
  'what is the difference between structural and rack supported mezzanine',
  'which is better steel or wire shelving',
  'what type of racking holds the most weight',
  'which shelving is best for heavy items',
  'what is the best racking for small warehouse',
  'cantilever vs pallet racking which to choose',

  // Installation Questions
  'how long does pallet racking installation take',
  'what is the process for installing shelving',
  'how to install mezzanine floor',
  'what do i need to install warehouse racking',
  'how to set up pallet racking',
  'what is required for shelving installation',
  'how to design warehouse layout',
  'what is the installation cost for racking',
  'how to prepare for mezzanine installation',
  'what permits do i need for mezzanine floor',

  // Safety Questions
  'what are the safety standards for pallet racking',
  'how to inspect warehouse racking',
  'what is sans compliance for shelving',
  'how often should racking be inspected',
  'what are the safety requirements for mezzanine floors',
  'how to maintain pallet racking',
  'what is the load capacity for industrial shelving',
  'how to ensure racking safety',
  'what are the regulations for warehouse storage',
  'how to check if racking is compliant',

  // B-BBEE Questions
  'what is bbbee level 1 supplier',
  'how to find bbbee certified suppliers',
  'what are the benefits of using bbbee suppliers',
  'how to verify bbbee certificate',
  'what is a level 1 bbbee contributor',
  'how does bbbee procurement work',
  'what companies are bbbee level 1',
  'how to get bbbee certificate for suppliers',
  'what is the difference between bbbee levels',
  'how to check bbbee status online',

  // ==========================================================
  // NEAR ME & LOCAL SEARCHES
  // ==========================================================

  // General Near Me
  'pallet racking near me',
  'industrial shelving near me',
  'mezzanine floor installers near me',
  'warehouse racking near me',
  'storage solutions near me',
  'shelving suppliers near me',
  'racking installation near me',
  'mezzanine builders near me',
  'storage system installers near me',
  'warehouse design services near me',

  // Major Cities
  'pallet racking johannesburg',
  'industrial shelving pretoria',
  'mezzanine floor durban',
  'warehouse racking cape town',
  'storage solutions port elizabeth',
  'shelving suppliers bloemfontein',
  'racking installation nelspruit',
  'mezzanine builders polokwane',
  'storage system installers rustenburg',
  'warehouse design kimberley',

  // Gauteng Areas
  'pallet racking midrand',
  'industrial shelving sandton',
  'mezzanine floor randburg',
  'warehouse racking centurion',
  'storage solutions boksburg',
  'shelving suppliers benoni',
  'racking installation kempton park',
  'mezzanine builders germiston',
  'storage system installers alberton',
  'warehouse design edenvale',

  // Vaal Triangle (Your Area!)
  'pallet racking suppliers sebokeng',
  'industrial shelving vereeniging',
  'mezzanine floor vanderbijlpark',
  'warehouse racking vaal triangle',
  'storage solutions sasolburg',
  'shelving suppliers heidelberg gauteng',
  'racking installation boipatong',
  'mezzanine builders bophelong',
  'storage systems meyerton',
  'shelving company sharpeville',

  // ==========================================================
  // VOICE SEARCH (Conversational)
  // ==========================================================

  'hey google find me pallet racking suppliers in johannesburg',
  'where can i buy industrial shelving near me',
  'what is the best mezzanine floor company in pretoria',
  'how do i choose the right pallet racking for my warehouse',
  'who installs warehouse racking in gauteng',
  'what is the cheapest shelving option for my store',
  'how much would a mezzanine floor cost for my warehouse',
  'where to find bbbee level 1 storage suppliers',
  'what type of racking do i need for my business',
  'who are the best industrial shelving companies in south africa',
  'how can i maximize my warehouse storage space',
  'what is the safest racking system for my warehouse',
  'where to get quotes for pallet racking installation',
  'how long does it take to install industrial shelving',
  'what companies sell mezzanine floors in durban',

  // ==========================================================
  // B2B & GOVERNMENT SEARCHES
  // ==========================================================

  'approved racking suppliers for government',
  'bbbee level 1 industrial suppliers',
  'warehouse equipment tender suppliers',
  'storage system procurement south africa',
  'industrial shelving vendors gauteng',
  'pallet racking contractors johannesburg',
  'mezzanine floor suppliers for government',
  'bbbee certified storage companies',
  'warehouse infrastructure suppliers',
  'storage solution providers south africa',
  'government tender racking suppliers',
  'bbbee suppliers for warehouse projects',
  'storage equipment tender applications',
  'industrial shelving tender documents',
  'pallet racking government contracts',

  // ==========================================================
  // PROBLEM-SOLUTION SEARCHES
  // ==========================================================

  'my warehouse is running out of space',
  'need more storage in my factory',
  'how to organize my warehouse better',
  'warehouse is disorganized and inefficient',
  'running out of shelf space in store',
  'need to store more pallets in same space',
  'warehouse layout is not working',
  'storage area is cluttered and unsafe',
  'cant find products in my warehouse',
  'need to double my storage capacity',
  'how to maximize warehouse space',
  'best way to organize warehouse',
  'how to increase storage capacity',
  'warehouse optimization solutions',
  'how to improve warehouse efficiency',

  // ==========================================================
  // SERVICE SEARCHES
  // ==========================================================

  'pallet racking installation companies',
  'industrial shelving installers',
  'mezzanine floor installation services',
  'warehouse racking installation johannesburg',
  'shelving installation pretoria',
  'racking installers gauteng',
  'mezzanine installation contractors',
  'storage system installation durban',
  'warehouse fit out services',
  'racking dismantling and relocation',
  'warehouse design consultants',
  'storage system design services',
  'warehouse layout planning',
  'storage consultation south africa',
  'warehouse optimization consultants',
  'pallet racking repair services',
  'shelving maintenance companies',
  'racking inspection services',
  'warehouse storage maintenance',
  'racking damage repair',

  // ==========================================================
  // COMPARISON & REVIEW SEARCHES
  // ==========================================================

  'best pallet racking companies south africa',
  'top industrial shelving suppliers',
  'mezzanine floor companies reviews',
  'compare racking suppliers johannesburg',
  'best shelving for warehouse',
  'top rated storage solution companies',
  'pallet racking price comparison',
  'industrial shelving reviews',
  'mezzanine floor contractor reviews',
  'compare warehouse storage systems',
  'molomo nr reviews',
  'molomo racking review',
  'molomo shelving testimonials',
  'molomo nr bbbee certificate',
  'molomo storage solutions review',

  // ==========================================================
  // MOBILE SHORT SEARCHES
  // ==========================================================

  'pallet racking za',
  'shelving jhb',
  'mezzanine pretoria',
  'racking suppliers',
  'shelving install',
  'mezzanine cost',
  'storage gauteng',
  'racking prices',
  'shelving quote',
  'mezzanine sqm price',

  // ==========================================================
  // YOUR ORIGINAL KEYWORDS (Keep These!)
  // ==========================================================

  'industrial storage',
  'pallet racking',
  'warehouse shelving',
  'mezzanine floors',
  'storage systems',
  'B-BBEE Level 1',
  'South Africa',
  'Sebokeng',
  'Gauteng',
  'industrial shelving',
  'warehouse racking',
  'storage lockers',
  'metal shelving',
  'heavy duty racking',
  'warehouse solutions',
  'storage solutions',
  'industrial racking',
  'steel shelving',
  'storage cabinets',
  'warehouse mezzanine',
    ...keywords
  ].join(', ');

  return (
    <>
      <Helmet>
        {/* Basic Meta */}
        <html lang="en-ZA" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <meta name="author" content={defaultSEO.siteName} />
        <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
        <meta name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
        <meta name="language" content="English" />
        
        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Viewport & Theme */}
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        <meta name="theme-color" content="#D4FF00" />
        <meta name="msapplication-TileColor" content="#1a1a1a" />
        
        {/* Location */}
        <meta name="geo.region" content="ZA-GT" />
        <meta name="geo.placename" content="Sebokeng, Gauteng" />
        <meta name="geo.position" content="-26.6167;27.8500" />
        <meta name="ICBM" content="-26.6167, 27.8500" />
        
        {/* Open Graph */}
        <meta property="og:type" content={type} />
        <meta property="og:locale" content="en_ZA" />
        <meta property="og:site_name" content={defaultSEO.siteName} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={fullImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${defaultSEO.siteName} - Industrial Storage Solutions`} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@molomonr" />
        <meta name="twitter:creator" content="@molomonr" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={fullImageUrl} />
        
        {/* Preconnect */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Alternate Languages */}
        <link rel="alternate" hrefLang="en-ZA" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateWebsiteSchema(pathname))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateLocalBusinessSchema())}
        </script>
        {pathname !== '/' && (
          <script type="application/ld+json">
            {JSON.stringify(generateBreadcrumbSchema(pathname))}
          </script>
        )}
      </Helmet>
      {children}
    </>
  );
};

export default SEOLayout;
